import React from 'react';
import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';

/**
 * ConnectionStatusButton component for displaying connection status or connect button
 * Provides consistent styling and messaging across all founder connection interfaces
 */
const ConnectionStatusButton = ({
  connectionStatus,
  onConnect,
  isConnecting = false,
  disabled = false,
  className = '',
  size = 'default', // 'default' | 'large'
  children = 'Connect with Founder'
}) => {
  // Handle connect button click
  const handleConnect = (e) => {
    e.stopPropagation();
    if (onConnect && typeof onConnect === 'function' && !isConnecting && !disabled) {
      onConnect();
    }
  };

  // Get size-specific classes
  const getSizeClasses = () => {
    if (size === 'large') {
      return 'px-6 py-3 text-base';
    }
    return 'px-4 py-2.5 sm:py-3 text-sm';
  };

  // Loading state
  if (connectionStatus.loading) {
    return (
      <button
        disabled
        className={`${getSizeClasses()} bg-gray-300 text-gray-500 font-medium 
                   rounded-lg flex items-center justify-center gap-2 cursor-wait
                   w-full ${className}`}
      >
        <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
        <span>Loading...</span>
      </button>
    );
  }

  // Connection status confirmation states
  if (connectionStatus.hasRequest) {
    return (
      <button
        disabled
        className={`${getSizeClasses()} bg-green-100 text-green-800 border border-green-200 font-medium 
                   rounded-lg transition-all duration-200 ease-out
                   flex items-center justify-center gap-2 cursor-default w-full
                   dark:bg-green-900/30 dark:text-green-300 dark:border-green-700 ${className}`}
      >
        {connectionStatus.status === 'introduced' ? (
          <>
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
            <span>Introduction Made</span>
          </>
        ) : connectionStatus.status === 'approved' ? (
          <>
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
            <span>Request Approved</span>
          </>
        ) : (
          <>
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
            </svg>
            <span>Request Sent</span>
          </>
        )}
      </button>
    );
  }

  // Connect button state
  return (
    <button
      onClick={handleConnect}
      disabled={disabled || isConnecting}
      className={`${getSizeClasses()} rounded-lg font-medium transition-all duration-200 
                 flex items-center justify-center gap-2 w-full ${
        disabled || isConnecting
          ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
          : 'bg-purple-600 hover:bg-purple-700 text-white hover:scale-105 hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800'
      } ${className}`}
    >
      {isConnecting ? (
        <>
          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
          <span>Connecting...</span>
        </>
      ) : (
        <>
          <span>{children}</span>
          {size === 'large' && <ArrowTopRightOnSquareIcon className="w-4 h-4" />}
        </>
      )}
    </button>
  );
};

export default ConnectionStatusButton;